import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import shiftsService from '../../../services/shiftsService';

const BulkActionsModal = ({ isOpen, onClose, selectedEmployees, onBulkAction }) => {
  const [activeAction, setActiveAction] = useState('');
  const [shifts, setShifts] = useState([]);
  const [loadingShifts, setLoadingShifts] = useState(false);
  const [actionData, setActionData] = useState({
    emailSubject: '',
    emailMessage: '',
    salaryIncrease: '',
    newDepartment: '',
    newStatus: '',
    newShiftId: '',
    attendanceDate: new Date().toISOString().split('T')[0],
    clockInTime: '09:00',
    clockOutTime: '18:00',
    attendanceStatus: 'present',
    attendanceNotes: ''
  });

  useEffect(() => {
    if (isOpen) {
      loadShifts();
    }
  }, [isOpen]);

  const loadShifts = async () => {
    try {
      setLoadingShifts(true);
      const result = await shiftsService.getShifts();
      if (result?.success) {
        setShifts(result.data || []);
      }
    } catch (error) {
      console.error('Failed to load shifts:', error);
    } finally {
      setLoadingShifts(false);
    }
  };

  const bulkActions = [
    {
      id: 'email',
      title: 'Send Email',
      description: 'Send email notification to selected employees',
      icon: 'Mail',
      color: 'bg-blue-500'
    },
    {
      id: 'salary',
      title: 'Update Salary',
      description: 'Apply salary increase to selected employees',
      icon: 'DollarSign',
      color: 'bg-green-500'
    },
    {
      id: 'shift',
      title: 'Update Shift',
      description: 'Manage rotational shifts for selected employees',
      icon: 'Clock',
      color: 'bg-indigo-500'
    },
    {
      id: 'department',
      title: 'Transfer Department',
      description: 'Move employees to different department',
      icon: 'Building',
      color: 'bg-purple-500'
    },
    {
      id: 'status',
      title: 'Update Status',
      description: 'Change employment status for selected employees',
      icon: 'UserCheck',
      color: 'bg-orange-500'
    },
    {
      id: 'attendance',
      title: 'Manual Attendance',
      description: 'Record attendance for selected employees',
      icon: 'Calendar',
      color: 'bg-red-500'
    },
    {
      id: 'export',
      title: 'Export Data',
      description: 'Download employee data in CSV format',
      icon: 'Download',
      color: 'bg-gray-500'
    }
  ];

  const handleActionSelect = (actionId) => {
    setActiveAction(actionId);
  };

  const handleInputChange = (field, value) => {
    setActionData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleExecuteAction = () => {
    const actionPayload = {
      action: activeAction,
      employeeIds: selectedEmployees,
      data: actionData
    };

    onBulkAction(actionPayload);
    onClose();

    // Reset form
    setActiveAction('');
    setActionData({
      emailSubject: '',
      emailMessage: '',
      salaryIncrease: '',
      newDepartment: '',
      newStatus: '',
      newShiftId: ''
    });
  };

  const renderActionForm = () => {
    switch (activeAction) {
      case 'email':
        return (
          <div className="space-y-4">
            <Input
              label="Email Subject"
              type="text"
              value={actionData.emailSubject}
              onChange={(e) => handleInputChange('emailSubject', e.target.value)}
              placeholder="Enter email subject"
              required
            />
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Message</label>
              <textarea
                value={actionData.emailMessage}
                onChange={(e) => handleInputChange('emailMessage', e.target.value)}
                rows={4}
                className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="Enter email message"
                required
              />
            </div>
          </div>
        );

      case 'salary':
        return (
          <div className="space-y-4">
            <Input
              label="Salary Increase (%)"
              type="number"
              value={actionData.salaryIncrease}
              onChange={(e) => handleInputChange('salaryIncrease', e.target.value)}
              placeholder="Enter percentage increase"
              min="0"
              max="100"
              required
            />
            <div className="bg-muted/50 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="Info" size={16} className="text-primary" />
                <span className="text-sm font-medium text-foreground">Important Note</span>
              </div>
              <p className="text-sm text-muted-foreground">
                This action will update the base salary for all selected employees.
                The change will be reflected in the next payroll cycle.
              </p>
            </div>
          </div>
        );

      case 'department':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">New Department</label>
              <select
                value={actionData.newDepartment}
                onChange={(e) => handleInputChange('newDepartment', e.target.value)}
                className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                required
              >
                <option value="">Select Department</option>
                <option value="Security Services">Security Services</option>
                <option value="Housekeeping">Housekeeping</option>
                <option value="Maintenance">Maintenance</option>
                <option value="Administration">Administration</option>
                <option value="Human Resources">Human Resources</option>
              </select>
            </div>
            <div className="bg-warning/10 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="AlertTriangle" size={16} className="text-warning" />
                <span className="text-sm font-medium text-foreground">Transfer Notice</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Department transfer will update employee records and may affect reporting structure.
              </p>
            </div>
          </div>
        );

      case 'status':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">New Status</label>
              <select
                value={actionData.newStatus}
                onChange={(e) => handleInputChange('newStatus', e.target.value)}
                className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                required
              >
                <option value="">Select Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="suspended">Suspended</option>
                <option value="probation">Probation</option>
              </select>
            </div>
          </div>
        );

      case 'shift':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Assign New Shift</label>
              {loadingShifts ? (
                <p className="text-sm text-muted-foreground">Loading shifts...</p>
              ) : (
                <select
                  value={actionData.newShiftId}
                  onChange={(e) => handleInputChange('newShiftId', e.target.value)}
                  className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  required
                >
                  <option value="">Select Shift</option>
                  {shifts.map(shift => (
                    <option key={shift.id} value={shift.id}>
                      {shift.name} ({shift.start_time.substring(0, 5)} - {shift.end_time.substring(0, 5)})
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div className="bg-muted/50 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="Info" size={16} className="text-primary" />
                <span className="text-sm font-medium text-foreground">Rotational Shift Help</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Use this to rotate employees between different shifts (e.g., swapping Morning and Night shifts). 
                Once applied, the new shift will be standard for all selected employees until their next rotation.
              </p>
            </div>
          </div>
        );

      case 'attendance':
        return (
          <div className="space-y-4">
            <Input
              label="Attendance Date"
              type="date"
              value={actionData.attendanceDate}
              onChange={(e) => handleInputChange('attendanceDate', e.target.value)}
              required
            />
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Clock In Time"
                type="time"
                value={actionData.clockInTime}
                onChange={(e) => handleInputChange('clockInTime', e.target.value)}
              />
              <Input
                label="Clock Out Time"
                type="time"
                value={actionData.clockOutTime}
                onChange={(e) => handleInputChange('clockOutTime', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Status</label>
              <select
                value={actionData.attendanceStatus}
                onChange={(e) => handleInputChange('attendanceStatus', e.target.value)}
                className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                required
              >
                <option value="present">Present</option>
                <option value="late">Late</option>
                <option value="half_day">Half Day</option>
                <option value="absent">Absent</option>
                <option value="early_departure">Early Departure</option>
                <option value="paid_leave">Paid Leave</option>
                <option value="public_holiday">Public Holiday</option>
                <option value="marked_weekly_off">Marked Weekly Off</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Notes</label>
              <textarea
                value={actionData.attendanceNotes}
                onChange={(e) => handleInputChange('attendanceNotes', e.target.value)}
                rows={2}
                className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="Entry reason or notes"
              />
            </div>
          </div>
        );

      case 'export':
        return (
          <div className="space-y-4">
            <div className="bg-muted/50 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="Download" size={16} className="text-primary" />
                <span className="text-sm font-medium text-foreground">Export Details</span>
              </div>
              <p className="text-sm text-muted-foreground mb-3">
                Export data for {selectedEmployees.length} selected employees including:
              </p>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Personal information</li>
                <li>• Employment details</li>
                <li>• Contact information</li>
                <li>• Department and designation</li>
              </ul>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-card rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Bulk Actions</h2>
            <p className="text-sm text-muted-foreground mt-1">
              {selectedEmployees.length} employee(s) selected
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Modal Content */}
        <div className="p-6 overflow-y-auto max-h-[70vh]">
          {!activeAction ? (
            // Action Selection
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-foreground mb-4">Choose an action</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {bulkActions.map((action) => (
                  <button
                    key={action.id}
                    onClick={() => handleActionSelect(action.id)}
                    className="p-4 border border-border rounded-lg hover:bg-muted/50 transition-colors text-left"
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`${action.color} p-2 rounded-lg`}>
                        <Icon name={action.icon} size={20} color="white" />
                      </div>
                      <h4 className="font-medium text-foreground">{action.title}</h4>
                    </div>
                    <p className="text-sm text-muted-foreground">{action.description}</p>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            // Action Form
            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setActiveAction('')}
                  iconName="ArrowLeft"
                >
                  Back
                </Button>
                <div>
                  <h3 className="text-lg font-medium text-foreground">
                    {bulkActions.find(a => a.id === activeAction)?.title}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Configure action for {selectedEmployees.length} employee(s)
                  </p>
                </div>
              </div>

              {renderActionForm()}
            </div>
          )}
        </div>

        {/* Modal Footer */}
        {activeAction && (
          <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
            <Button variant="outline" onClick={() => setActiveAction('')}>
              Back
            </Button>
            <Button onClick={handleExecuteAction}>
              Execute Action
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkActionsModal;